import { parseActionError } from './adminUi.js'
import { toPositiveIntOr } from './contracts.js'

export const TICKET_LIFECYCLE = {
  available: { label: 'Available', tone: 'neutral', readOnly: false },
  reserved: { label: 'Reserved', tone: 'warning', readOnly: false },
  assigned: { label: 'Assigned', tone: 'active', readOnly: false },
  sold: { label: 'Sold', tone: 'success', readOnly: false },
  refunded: { label: 'Refunded', tone: 'warning', readOnly: false },
  voided: { label: 'Voided', tone: 'danger', readOnly: true },
  winner: { label: 'Winner', tone: 'success', readOnly: true },
  archived: { label: 'Archived', tone: 'deleted', readOnly: true },
}

const LIFECYCLE_BLOCKED_ASSIGNMENT = new Set(['closed', 'drawn', 'archived'])

const STATE_RANK = {
  available: 0,
  assigned: 1,
  reserved: 2,
  sold: 3,
  refunded: 4,
  voided: 5,
  winner: 6,
  archived: 7,
}

const toTicketNumber = (value) => {
  const parsed = toPositiveIntOr(value, -1)
  return parsed > 0 ? parsed : null
}

const toRange = (start, end) => {
  const normalizedStart = toTicketNumber(start)
  const normalizedEnd = toTicketNumber(end)
  if (!normalizedStart || !normalizedEnd || normalizedEnd < normalizedStart) return []
  const values = []
  for (let current = normalizedStart; current <= normalizedEnd; current += 1) {
    values.push(current)
  }
  return values
}

export const getTicketLifecycleMeta = (state) => {
  const key = String(state || '').trim().toLowerCase()
  return TICKET_LIFECYCLE[key] || TICKET_LIFECYCLE.available
}

export const isTicketReadOnly = (state) => getTicketLifecycleMeta(state).readOnly

export const canAssignTicketsForLotteryStatus = (lotteryStatus) => {
  const normalized = String(lotteryStatus || '').trim().toLowerCase()
  return !LIFECYCLE_BLOCKED_ASSIGNMENT.has(normalized)
}

export const canTransitionTicketState = (fromState, toState) => {
  const from = String(fromState || '').trim().toLowerCase() || 'available'
  const to = String(toState || '').trim().toLowerCase()
  if (!TICKET_LIFECYCLE[to]) return false
  if (from === to) return false
  if (isTicketReadOnly(from)) return false

  if (to === 'reserved') return from === 'available' || from === 'assigned'
  if (to === 'assigned') return from === 'available' || from === 'reserved'
  if (to === 'sold') return from === 'reserved' || from === 'assigned'
  if (to === 'refunded') return from === 'sold'
  if (to === 'voided') return from !== 'winner' && from !== 'archived'

  return false
}

export const normalizeTicketHistory = (history) => {
  if (!Array.isArray(history)) return []
  return history.map((entry, index) => {
    const payload = entry && typeof entry === 'object' ? entry : {}
    const ticketNumber = toTicketNumber(payload.ticket_number ?? payload.ticket ?? payload.number)
    return {
      id: payload.id ?? payload.event_id ?? `ticket-event-${index}`,
      ticketNumber,
      fromState: String(payload.from_state ?? payload.previous_state ?? 'available').trim().toLowerCase(),
      toState: String(payload.to_state ?? payload.state ?? 'available').trim().toLowerCase(),
      actor: payload.actor ?? payload.username ?? payload.updated_by ?? null,
      createdAt: payload.created_at ?? payload.timestamp ?? null,
      notes: payload.notes ?? '',
      ownerUserId: payload.user_id ?? payload.owner_user_id ?? null,
    }
  }).filter((item) => item.ticketNumber !== null)
}

const upsertTicketRecord = (store, ticketNumber, patch) => {
  const current = store.get(ticketNumber) || {
    ticketNumber,
    state: 'available',
    ownerUserId: null,
    history: [],
  }

  store.set(ticketNumber, {
    ...current,
    ...patch,
  })
}

const rankState = (state) => {
  const key = String(state || '').trim().toLowerCase()
  return STATE_RANK[key] ?? -1
}

const normalizeBatchSoldTicketToGlobal = (batch, soldTicket) => {
  const ticketNumber = toTicketNumber(soldTicket)
  if (!ticketNumber) return null

  const globalStart = toTicketNumber(batch?.global_start)
  const globalEnd = toTicketNumber(batch?.global_end)
  if (!globalStart || !globalEnd || globalEnd < globalStart) {
    return ticketNumber
  }

  const rangeSize = globalEnd - globalStart + 1
  if (ticketNumber >= globalStart && ticketNumber <= globalEnd) {
    // Already globalized by backend.
    return ticketNumber
  }

  // Some payloads provide local sheet numbers (1..N); normalize to global range.
  if (ticketNumber >= 1 && ticketNumber <= rangeSize) {
    return globalStart + ticketNumber - 1
  }

  return ticketNumber
}

const flattenBatchPayload = (batches) => {
  const input = Array.isArray(batches) ? batches : []
  const flattened = []

  for (const batch of input) {
    if (!batch || typeof batch !== 'object') continue

    const nestedUploads = Array.isArray(batch.uploads) ? batch.uploads : []
    if (!nestedUploads.length) {
      flattened.push(batch)
      continue
    }

    for (const upload of nestedUploads) {
      if (!upload || typeof upload !== 'object') continue
      flattened.push({
        ...batch,
        ...upload,
        user_id: upload?.user_id ?? batch?.user_id ?? null,
      })
    }
  }

  return flattened
}

export const buildTicketLifecycleRecords = ({ history = [], assignments = [], batches = [], drawHistory = [] }) => {
  const records = new Map()

  for (const assignment of Array.isArray(assignments) ? assignments : []) {
    const ownerUserId = assignment?.user_id ?? null
    for (const ticketNumber of toRange(assignment?.global_start, assignment?.global_end)) {
      upsertTicketRecord(records, ticketNumber, {
        state: 'assigned',
        ownerUserId,
      })
    }
  }

  for (const batch of flattenBatchPayload(batches)) {
    const soldTickets = Array.isArray(batch?.sold_tickets) ? batch.sold_tickets : []
    const ownerUserId = batch?.user_id ?? null
    for (const soldTicket of soldTickets) {
      const ticketNumber = normalizeBatchSoldTicketToGlobal(batch, soldTicket)
      if (!ticketNumber) continue
      upsertTicketRecord(records, ticketNumber, {
        state: 'sold',
        ownerUserId,
      })
    }
  }

  for (const drawEntry of Array.isArray(drawHistory) ? drawHistory : []) {
    const winners = Array.isArray(drawEntry?.winners)
      ? drawEntry.winners
      : drawEntry?.winner
        ? [drawEntry.winner]
        : [drawEntry]

    for (const winner of winners) {
      const ticketNumber = toTicketNumber(winner?.ticket_number ?? winner?.winning_number)
      if (!ticketNumber) continue
      upsertTicketRecord(records, ticketNumber, {
        state: 'winner',
        ownerUserId: winner?.user_id ?? records.get(ticketNumber)?.ownerUserId ?? null,
      })
    }
  }

  for (const event of normalizeTicketHistory(history)) {
    const current = records.get(event.ticketNumber)
    const fromRank = rankState(current?.state)
    const toRank = rankState(event.toState)

    upsertTicketRecord(records, event.ticketNumber, {
      state: toRank >= fromRank ? event.toState : current?.state || 'available',
      ownerUserId: event.ownerUserId ?? current?.ownerUserId ?? null,
      history: [...(current?.history || []), event],
    })
  }

  return [...records.values()].sort((a, b) => a.ticketNumber - b.ticketNumber)
}

export const summarizeTicketLifecycle = (records) => {
  const summary = {
    total: 0,
    assigned: 0,
    sold: 0,
    winner: 0,
  }

  for (const row of Array.isArray(records) ? records : []) {
    summary.total += 1
    if (row.state === 'assigned') summary.assigned += 1
    if (row.state === 'sold') summary.sold += 1
    if (row.state === 'winner') summary.winner += 1
  }

  return summary
}

export const buildTicketIntegrityWarnings = (records) => {
  const rows = Array.isArray(records) ? records : []
  const ownerByTicket = new Map()
  const warnings = []
  let invalidLifecycleStates = 0
  let duplicateOwnershipConflicts = 0

  for (const row of rows) {
    if (!TICKET_LIFECYCLE[row.state]) {
      invalidLifecycleStates += 1
    }

    const existingOwner = ownerByTicket.get(row.ticketNumber)
    if (existingOwner && row.ownerUserId && existingOwner !== row.ownerUserId) {
      duplicateOwnershipConflicts += 1
    }

    if (row.ownerUserId) {
      ownerByTicket.set(row.ticketNumber, row.ownerUserId)
    }
  }

  if (invalidLifecycleStates > 0) {
    warnings.push(`Data inconsistency (frontend-derived): unsupported ticket states detected (${invalidLifecycleStates}).`)
  }
  if (duplicateOwnershipConflicts > 0) {
    warnings.push(`Data inconsistency (frontend-derived): duplicate ownership conflicts detected (${duplicateOwnershipConflicts}).`)
  }

  return {
    invalidLifecycleStates,
    duplicateOwnershipConflicts,
    warnings,
  }
}

export const parseTicketOperationError = (err, fallback = 'Ticket operation failed.') => {
  return parseActionError(err, fallback, {
    conflict: 'Ticket state changed on the server. Refresh and retry the operation.',
    forbidden: 'You do not have permission to update ticket lifecycle for this lottery.',
    notFound: 'Ticket resource was not found. Refresh and retry.',
  })
}

